import React from 'react';
import { TicketReply, TicketAttachment, Ticket, TicketStatus } from './types';
import { UserAvatar, TimeAgo } from './TicketShared';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Paperclip, Send, Lock, FileText, X, Pencil, Trash2, Check, MoreVertical, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { uploadToR2 } from '@/lib/r2-storage';
import { cn } from '@/lib/utils';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

interface TicketConversationProps {
  ticket: Ticket;
  replies: TicketReply[];
  onAddReply: (content: string, isInternal: boolean, attachments: TicketAttachment[]) => Promise<void>;
  onUpdateReply: (replyId: string, content: string) => Promise<void>;
  onDeleteReply: (replyId: string) => Promise<void>;
  onChangeStatus?: (status: TicketStatus) => Promise<void>;
  submitting: boolean;
  isTeamMember: boolean;
  isClientMessage: (userId?: string) => boolean;
}

export const TicketConversation: React.FC<TicketConversationProps> = ({
  ticket, replies, onAddReply, onUpdateReply, onDeleteReply, onChangeStatus, submitting, isTeamMember, isClientMessage
}) => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'conversation' | 'notes'>('conversation');
  const [content, setContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Lightbox State
  const [previewData, setPreviewData] = useState<{
    attachments: TicketAttachment[];
    currentIndex: number;
  } | null>(null);

  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, []);

  const openPreview = (attachments: TicketAttachment[], index: number) => {
    resetZoom();
    setPreviewData({ attachments, currentIndex: index });
  };

  const closePreview = () => {
    resetZoom();
    setPreviewData(null);
  };

  const handleNext = useCallback((e?: React.MouseEvent | KeyboardEvent) => {
    e?.stopPropagation();
    resetZoom();
    setPreviewData(prev => prev ? ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.attachments.length
    }) : null);
  }, [resetZoom]);

  const handlePrevious = useCallback((e?: React.MouseEvent | KeyboardEvent) => {
    e?.stopPropagation();
    resetZoom();
    setPreviewData(prev => prev ? ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.attachments.length) % prev.attachments.length
    }) : null);
  }, [resetZoom]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!previewData) return;
      if (e.key === 'Escape') closePreview();
      if (e.key === 'ArrowLeft') handlePrevious(e);
      if (e.key === 'ArrowRight') handleNext(e);
      if (e.key === 'r' || e.key === 'R') resetZoom();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewData, handlePrevious, handleNext, resetZoom]);

  const handleZoomWheel = (e: React.WheelEvent) => {
    const currentFile = previewData ? previewData.attachments[previewData.currentIndex] : null;
    if (currentFile?.type.startsWith('image/')) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(prev => Math.min(Math.max(1, prev + delta), 4));
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  const currentFile = previewData ? previewData.attachments[previewData.currentIndex] : null;

  // Auto-scroll to bottom when new replies arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [replies, activeTab]);

  useEffect(() => {
    if (isTeamMember) {
      setIsInternal(activeTab === 'notes');
    }
  }, [activeTab, isTeamMember]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) { toast.error(`${file.name} is too large (max 5MB)`); continue; }
      if (!file.type.match(/^image\/(jpeg|png)$/)) { toast.error(`${file.name} is not a valid image format (JPG/PNG only)`); continue; }
      setUploading(true);
      try {
        const { publicUrl } = await uploadToR2({ fileName: `reply-${Date.now()}-${file.name}`, file, folder: 'ticket-attachments' });
        setAttachments(prev => [...prev, { name: file.name, url: publicUrl, size: file.size, type: file.type }]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
      finally { setUploading(false); }
    }
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!content.trim() || submitting) return;
    await onAddReply(content.trim(), isInternal, attachments);
    setContent(''); setAttachments([]);
    if (activeTab === 'conversation') setIsInternal(false);
  };

  const handleEditSubmit = async (replyId: string) => {
    if (!editContent.trim() || editSubmitting) return;
    setEditSubmitting(true);
    try {
      await onUpdateReply(replyId, editContent.trim());
      setEditingReplyId(null);
    } catch (err) {
      toast.error('Failed to update message');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (replyId: string) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    setEditSubmitting(true);
    try {
      await onDeleteReply(replyId);
    } catch (err) {
      toast.error('Failed to delete message');
    } finally {
      setEditSubmitting(false);
    }
  };

  // Helper to render a message bubble
  const renderMessage = (
    id: string,
    userId: string | undefined,
    name: string | undefined,
    avatar: string | undefined,
    date: string,
    msgContent: string,
    msgAttachments: TicketAttachment[] = [],
    isInternalMsg = false,
    hideHeader = false,
    isConsecutive = false,
    isEditable = false
  ) => {
    const isOwn = userId === profile?.id;
    const isClient = isClientMessage(userId);
    const alignRight = isTeamMember === isClient;

    return (
      <div key={id} className={cn("flex flex-col w-full", isConsecutive ? "mb-1.5" : "mb-6", alignRight ? "items-end" : "items-start")}>
        <div className={cn("flex flex-col w-fit max-w-[95%] sm:max-w-[85%]", alignRight ? "items-end" : "items-start")}>
          {/* Header: Avatar, Name, Time */}
          {!hideHeader && (
            <div className={cn("flex items-center justify-between w-full px-1 mb-1.5 gap-4", alignRight ? "flex-row-reverse" : "flex-row")}>
              <div className={cn("flex items-center gap-2", alignRight ? "flex-row-reverse" : "flex-row")}>
                <UserAvatar name={name} avatar={avatar} size="sm" />
                <span className="text-xs font-semibold text-foreground">{name || 'Unknown'}</span>
                {isInternalMsg && (
                  <span className="flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded-full">
                    <Lock className="w-3 h-3" /> Internal
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap"><TimeAgo date={date} /></span>
            </div>
          )}

          {/* Bubble */}
          <div className="relative group">
            {editingReplyId === id ? (
              <div className={cn(
                "p-3 rounded-2xl text-sm shadow-sm w-full min-w-[200px] md:min-w-[300px]",
                alignRight ? "mr-9" : "ml-9",
                isOwn ? "bg-workspace-accent text-white" : isInternalMsg ? "bg-amber-500/10 border border-amber-500/20 text-foreground" : "bg-muted border border-border/50 text-foreground"
              )}>
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full min-h-[80px] bg-white/10 border-white/20 text-current placeholder:text-current/50 focus-visible:ring-white/30 resize-none text-sm p-2 mb-2"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditSubmit(id); }
                    if (e.key === 'Escape') setEditingReplyId(null);
                  }}
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setEditingReplyId(null)} className="h-7 px-2 text-current hover:bg-white/20 hover:text-current">Cancel</Button>
                  <Button size="sm" onClick={() => handleEditSubmit(id)} disabled={editSubmitting || !editContent.trim()} className="h-7 px-2 bg-white text-workspace-accent hover:bg-white/90">
                    <Check className="w-3.5 h-3.5 mr-1" /> Save
                  </Button>
                </div>
              </div>
            ) : (
              <div 
                className={cn(
                  "px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap shadow-sm",
                  hideHeader ? "mt-0.5" : "",
                  alignRight 
                    ? cn("rounded-tr-sm mr-9", hideHeader && "rounded-tr-2xl") 
                    : cn("rounded-tl-sm ml-9", hideHeader && "rounded-tl-2xl"),
                  isConsecutive
                    ? alignRight ? "rounded-br-sm" : "rounded-bl-sm"
                    : "",
                  isOwn 
                    ? "bg-workspace-accent text-white" 
                    : isInternalMsg 
                      ? "bg-amber-500/10 border border-amber-500/20 text-foreground"
                      : "bg-muted border border-border/50 text-foreground"
                )}
              >
                {msgContent}
              </div>
            )}

            {isEditable && editingReplyId !== id && id !== 'ticket-desc' && (
              <div className={cn(
                "absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity",
                alignRight ? "right-full mr-4" : "left-full ml-4"
              )}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align={alignRight ? "end" : "start"} className="w-32 border-0 shadow-md">
                    <DropdownMenuItem onClick={() => { setEditingReplyId(id); setEditContent(msgContent); }} className="cursor-pointer">
                      <Pencil className="w-4 h-4 mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDelete(id)} className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer">
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          {/* Attachments */}
          {msgAttachments && msgAttachments.length > 0 && (
            <div className={cn("flex flex-wrap gap-2 mt-2 w-full", alignRight ? "justify-end pr-9" : "justify-start pl-9")}>
              {msgAttachments.map((file, i) => (
                <div key={i} className="group/attachment relative transition-transform hover:scale-[1.01]">
                  {file.type && file.type.startsWith('image/') ? (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openPreview(msgAttachments, i);
                      }}
                      className="relative rounded-lg overflow-hidden border border-border max-w-[200px] max-h-[200px] cursor-zoom-in cursor-pointer shadow-sm"
                    >
                      <img src={file.url} alt={file.name} className="object-cover max-w-full max-h-[200px] w-auto h-auto" />
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openPreview(msgAttachments, i);
                      }}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-left shadow-sm",
                        isOwn 
                          ? "bg-workspace-accent/10 border-workspace-accent/20 text-foreground hover:bg-workspace-accent/20" 
                          : "bg-muted border-border text-foreground hover:bg-accent"
                      )}
                    >
                      <FileText className="w-4 h-4 opacity-70" />
                      <div className="flex flex-col">
                        <span className="text-xs font-medium leading-none mb-0.5 truncate max-w-[140px]">{file.name}</span>
                        {file.size && <span className="text-[10px] opacity-60">{Math.round(file.size / 1024)}KB</span>}
                      </div>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );

  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {isTeamMember && (
        <div className="flex items-center gap-6 border-b border-border/50 mb-4 flex-shrink-0">
          <button
            onClick={() => setActiveTab('conversation')}
            className={cn(
              "text-sm font-semibold pb-3 border-b-2 transition-colors",
              activeTab === 'conversation' ? "border-workspace-accent text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Conversation ({replies.filter(r => !r.is_internal).length})
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={cn(
              "text-sm font-semibold pb-3 border-b-2 transition-colors",
              activeTab === 'notes' ? "border-amber-500 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Internal Notes ({replies.filter(r => r.is_internal).length})
          </button>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto pr-2 mb-4 scrollbar-thin">
        {activeTab === 'conversation' && (
          <>
            {(() => {
              const publicReplies = replies.filter(r => !r.is_internal);
              const messages = [
                {
                  id: 'ticket-desc',
                  user_id: ticket.created_by,
                  name: ticket.creator?.name || undefined,
                  avatar: ticket.creator?.avatar || undefined,
                  created_at: ticket.created_at,
                  content: ticket.description,
                  attachments: ticket.attachments || [],
                  is_internal: false
                },
                ...publicReplies.map(r => ({
                  id: r.id,
                  user_id: r.user_id,
                  name: r.user?.name || undefined,
                  avatar: r.user?.avatar || undefined,
                  created_at: r.created_at,
                  content: r.content,
                  attachments: r.attachments || [],
                  is_internal: r.is_internal
                }))
              ];

              return messages.map((msg, idx) => {
                const prev = messages[idx - 1];
                const next = messages[idx + 1];
                const hideHeader = prev && prev.user_id === msg.user_id && prev.is_internal === msg.is_internal;
                const isConsecutive = next && next.user_id === msg.user_id && next.is_internal === msg.is_internal;
                const isEditable = msg.user_id === profile?.id && !messages.slice(idx + 1).some(m => m.user_id !== profile?.id);

                return renderMessage(
                  msg.id,
                  msg.user_id,
                  msg.name,
                  msg.avatar,
                  msg.created_at,
                  msg.content,
                  msg.attachments,
                  msg.is_internal,
                  hideHeader,
                  isConsecutive,
                  isEditable
                );
              });
            })()}
          </>
        )}

        {activeTab === 'notes' && (
          <>
            {(() => {
              const notes = replies.filter(r => r.is_internal);
              if (notes.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Lock className="w-10 h-10 text-amber-500/20 mb-3" />
                    <p className="text-sm text-muted-foreground">No internal notes yet.</p>
                  </div>
                );
              }

              return notes.map((msg, idx) => {
                const prev = notes[idx - 1];
                const next = notes[idx + 1];
                const hideHeader = prev && prev.user_id === msg.user_id;
                const isConsecutive = next && next.user_id === msg.user_id;
                const isEditable = msg.user_id === profile?.id && !notes.slice(idx + 1).some(m => m.user_id !== profile?.id);

                return renderMessage(
                  msg.id,
                  msg.user_id,
                  msg.user?.name || undefined,
                  msg.user?.avatar || undefined,
                  msg.created_at,
                  msg.content,
                  msg.attachments || [],
                  msg.is_internal,
                  hideHeader,
                  isConsecutive,
                  isEditable
                );
              });
            })()}
          </>
        )}
      </div>

      {/* Composer */}
      {ticket.status !== 'closed' ? (
        <div className={cn('flex-shrink-0 rounded-xl border overflow-hidden transition-colors shadow-sm', isInternal ? 'border-amber-500/50 bg-amber-500/5' : 'border-border bg-background')}>
          {isInternal && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/30">
              <Lock className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-medium text-amber-600">Internal note — only visible to your team</span>
            </div>
          )}
          <Textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSubmit(); } }}
            placeholder={isInternal ? 'Add an internal note...' : 'Write a reply... (Ctrl+Enter to send)'}
            className="border-0 shadow-none focus-visible:ring-0 resize-none min-h-[100px] max-h-[250px] bg-transparent text-foreground p-4"
          />
          {attachments.length > 0 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {attachments.map((att, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted text-sm border border-border">
                  <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-foreground truncate max-w-[140px] font-medium">{att.name}</span>
                  <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between px-3 py-2.5 border-t border-border/50 bg-background">
            <div className="flex items-center gap-2">
              <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                <Paperclip className="w-4 h-4" />{uploading && <span className="text-xs font-medium">Uploading...</span>}
              </button>
              <input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" ref={fileRef} multiple onChange={handleFileUpload} className="hidden" />
            </div>
            <Button onClick={handleSubmit} disabled={!content.trim() || submitting || uploading} size="sm" className={cn('gap-2 h-9 shadow-sm', isInternal ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-workspace-accent hover:bg-workspace-accent-hover text-white')}>
              <Send className="w-3.5 h-3.5" />
              <span className="font-medium">{submitting ? 'Sending...' : isInternal ? 'Add Note' : 'Send Reply'}</span>
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between p-4 bg-muted/30 border border-border rounded-xl mt-2 flex-shrink-0 gap-4">
          <div className="flex items-center gap-3 text-left">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
               <Lock className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">This ticket has been closed</p>
              {!isTeamMember && (
                <p className="text-xs text-muted-foreground">
                  If you need further assistance, please create a new ticket or contact support.
                </p>
              )}
            </div>
          </div>
          {onChangeStatus && (
            <Button
              size="sm"
              onClick={() => onChangeStatus('open')}
              className="flex-shrink-0 bg-workspace-accent hover:bg-workspace-accent-hover text-white shadow-sm"
            >
              Reopen ticket
            </Button>
          )}
        </div>
      )}

      {/* Lightbox / Preview Modal */}
      {currentFile && createPortal(
        <div
          className="fixed inset-0 z-[99999] flex flex-col bg-black/95 backdrop-blur-sm animate-in fade-in duration-200 pointer-events-auto"
          onClick={(e) => {
            e.stopPropagation();
            closePreview();
          }}
        >
          {/* Toolbar */}
          <div
            className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-black/40 text-white border-b border-white/10"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex flex-col min-w-0 flex-1">
              <h3 className="font-medium text-sm text-white/90 truncate">{currentFile.name}</h3>
              <span className="text-xs text-white/50">{currentFile.type || 'Unknown Type'} · {previewData && `${previewData.currentIndex + 1} of ${previewData.attachments.length}`}</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <a
                href={currentFile.url}
                download={currentFile.name}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/70 hover:text-white"
                title="Download"
              >
                <Download className="w-5 h-5" />
              </a>
              <button
                onClick={closePreview}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/70 hover:text-white"
                title="Close"
              >
                <X className="w-5 sm:w-6 h-5 sm:h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div
            className="flex-1 flex items-center justify-center p-4 overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Navigation Buttons */}
            {previewData && previewData.attachments.length > 1 && (
              <>
                <button
                  onClick={handlePrevious}
                  className="absolute left-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-[10000]"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={handleNext}
                  className="absolute right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-[10000]"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}

            {currentFile.type && currentFile.type.startsWith('image/') ? (
              <div 
                className="w-full h-full flex items-center justify-center overflow-hidden"
                onWheel={handleZoomWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <img
                  key={currentFile.url}
                  src={currentFile.url}
                  alt={currentFile.name}
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                    transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                    cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                  }}
                  className="max-w-full max-h-full object-contain shadow-2xl rounded-sm animate-in fade-in duration-200 select-none pointer-events-none"
                  draggable={false}
                />
                
                {/* Zoom Level Indicator */}
                {zoom > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      resetZoom();
                    }}
                    className="absolute bottom-6 right-6 px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-[10px] font-bold tracking-widest uppercase rounded-full backdrop-blur-md border border-white/10 transition-all hover:scale-105 active:scale-95 animate-in fade-in zoom-in duration-200 z-[10001]"
                  >
                    {Math.round(zoom * 100)}% · Reset
                  </button>
                )}
              </div>
            ) : (
              <iframe
                key={currentFile.url}
                src={currentFile.url}
                className="w-full h-full max-w-5xl bg-white rounded-md shadow-2xl animate-in fade-in duration-200"
                title={currentFile.name}
              />
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
